import { useContext, useState } from "react";

import { Modal, View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Platform } from "react-native";

import { KeyboardAvoidingView } from "react-native-keyboard-controller";

import { appColors, cardShadow } from "../../constants/colors";
import { CalculationsContext } from "../../store/calculationsCtx";
import { useWebLayout } from "../../hooks/useWebLayout";
import Dropdown from "../Dropdown";

const loanTypes = ["Car Finance", "Home Loan", "Personal Loan"];

const DEFAULT_TERMS = {
  "Car Finance": 72,
  "Home Loan": 240,
  "Personal Loan": 24,
};

const CreateCalculatorModal = ({ visible, onClose, onCreate, calculators }) => {
  const [name, setName] = useState("");
  const [selectedLoan, setSelectedLoan] = useState("Car Finance");
  const [loanAmount, setLoanAmount] = useState("");
  const [rate, setRate] = useState("");
  const [term, setTerm] = useState(String(DEFAULT_TERMS["Car Finance"]));

  const calculationsCtx = useContext(CalculationsContext);
  const { appWidth } = useWebLayout();
  const containerWidth = appWidth * 0.9;

  const onLoanTypeChange = type => {
    setSelectedLoan(type);
    setTerm(String(DEFAULT_TERMS[type]));
  };

  const parseNum = val => +String(val).replace(",", ".");

  const isValid =
    name.trim() &&
    selectedLoan &&
    calculationsCtx.currency &&
    loanAmount &&
    parseNum(loanAmount) > 0 &&
    rate &&
    parseNum(rate) > 0 &&
    term &&
    parseNum(term) > 0;

  const handleCreate = () => {
    if (!isValid) return;
    onCreate({
      name: name.trim(),
      loanType: selectedLoan,
      currency: calculationsCtx.currency,
      loanAmount: parseNum(loanAmount),
      rate: parseNum(rate),
      term: parseNum(term),
    });
    // Reset form
    setName("");
    setSelectedLoan("Car Finance");
    setLoanAmount("");
    setRate("");
    setTerm(String(DEFAULT_TERMS["Car Finance"]));
    onClose();
  };

  return (
    <Modal transparent visible={visible} animationType="slide" onRequestClose={onClose}>
      <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === "ios" ? "padding" : "height"}>
        <View style={[styles.container, { width: containerWidth }]}>
          <Text style={styles.title}>New calculator</Text>
          <Text style={styles.subtitle}>Give it a name and choose a type</Text>

          <TextInput
            style={styles.input}
            placeholder="e.g. My first car"
            placeholderTextColor={appColors.textMuted}
            value={name}
            onChangeText={setName}
          />

          <Dropdown />

          <View style={styles.loanTypeRow}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {loanTypes.map(loan => (
                <TouchableOpacity
                  key={loan}
                  style={[styles.loanBtn, selectedLoan === loan && styles.loanBtnActive]}
                  onPress={() => onLoanTypeChange(loan)}
                >
                  <Text style={[styles.loanBtnText, selectedLoan === loan && styles.loanBtnTextActive]}>{loan}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          <View style={styles.row}>
            <View style={styles.fieldHalf}>
              <Text style={styles.fieldLabel}>Loan amount</Text>
              <TextInput
                style={styles.input}
                placeholder="0"
                placeholderTextColor={appColors.textMuted}
                keyboardType={Platform.OS === "ios" ? "decimal-pad" : "numeric"}
                value={loanAmount}
                onChangeText={setLoanAmount}
              />
            </View>
            <View style={styles.fieldHalf}>
              <Text style={styles.fieldLabel}>Interest rate (%)</Text>
              <TextInput
                style={styles.input}
                placeholder="0"
                placeholderTextColor={appColors.textMuted}
                keyboardType={Platform.OS === "ios" ? "decimal-pad" : "numeric"}
                value={rate}
                onChangeText={setRate}
              />
            </View>
          </View>

          <View>
            <Text style={styles.fieldLabel}>Term (months) — auto-set for {selectedLoan}</Text>
            <TextInput
              style={styles.input}
              placeholder="0"
              placeholderTextColor={appColors.textMuted}
              keyboardType={Platform.OS === "ios" ? "decimal-pad" : "numeric"}
              value={term}
              onChangeText={setTerm}
            />
          </View>

          <TouchableOpacity
            style={[styles.createBtn, !isValid && styles.createBtnDisabled]}
            onPress={handleCreate}
            disabled={!isValid}
          >
            <Text style={styles.createBtnText}>Create calculator</Text>
          </TouchableOpacity>

          {calculators !== 0 && (
            <TouchableOpacity onPress={onClose} style={styles.cancelBtn}>
              <Text style={styles.cancelBtnText}>Cancel</Text>
            </TouchableOpacity>
          )}
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

export default CreateCalculatorModal;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(5,30,60,0.6)",
    justifyContent: "center",
    alignItems: "center",
  },

  container: {
    backgroundColor: appColors.cardBg,
    borderRadius: 20,
    padding: 22,
    ...cardShadow("large"),

    gap: 12,
  },

  title: {
    fontSize: 18,
    fontWeight: "500",
    color: appColors.textPrimary,
    textAlign: "center",
  },

  subtitle: {
    fontSize: 12,
    color: appColors.textMuted,
    textAlign: "center",
    marginTop: -4,
    marginBottom: 4,
  },

  row: {
    flexDirection: "row",
    gap: 10,
  },

  fieldHalf: {
    flex: 1,
  },

  fieldLabel: {
    fontSize: 11,
    fontWeight: "500",
    color: appColors.textSecondary,
    marginBottom: 4,
  },

  input: {
    width: "100%",
    borderWidth: 1,
    borderColor: appColors.border,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 11,
    fontSize: 13,
    color: appColors.textPrimary,
    backgroundColor: appColors.inputBg,
  },

  loanTypeRow: {
    marginTop: 2,
  },

  loanBtn: {
    paddingVertical: 7,
    paddingHorizontal: 14,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: appColors.border,
    marginRight: 8,
    backgroundColor: appColors.cardBg,
  },

  loanBtnActive: {
    backgroundColor: appColors.primary,
    borderColor: appColors.primary,
  },

  loanBtnText: {
    fontSize: 12,
    color: appColors.primary,
    fontWeight: "500",
  },

  loanBtnTextActive: {
    color: "#fff",
  },

  createBtn: {
    width: "100%",
    backgroundColor: appColors.primary,
    paddingVertical: 13,
    borderRadius: 12,
    alignItems: "center",
    marginTop: 4,
  },

  createBtnDisabled: {
    opacity: 0.45,
  },

  createBtnText: {
    color: "#fff",
    fontWeight: "500",
    fontSize: 14,
  },

  cancelBtn: {
    alignItems: "center",
    paddingVertical: 6,
  },

  cancelBtnText: {
    color: appColors.danger,
    fontSize: 13,
  },
});
